import React, { useMemo, useState, useEffect } from 'react';
import { authUtils } from '../../services/auth';
import { apiService } from '../../services/api';
import './Leaderboard.css';

interface TeamMember {
  username: string;
  nickname?: string;
}

interface LeaderboardEntry {
  username: string;
  nickname?: string;
  score: number;
  score_max?: number;
  evaluation_mode?: string;
  evaluation_count?: number;
  criterion_scores?: { criterion_id: number; label: string; average_score: number }[];
  /** Input tokens the graded runs sent. null when the meter did not report. */
  tokens?: number | null;
  /** Wall time of the graded run, in seconds. */
  duration_seconds?: number | null;
  emoji: string;
  team_members?: TeamMember[];
  submission_url?: string | null;
  consent_status?: string;
}

interface LeaderboardProps {
  competitionId: number;
  publicMode?: boolean;
  leaderboardMode?: string;
  competitionMode?: string;
}

/** 125400 -> "125k". A dash when the meter reported nothing, never a zero:
 *  zero would read as a free run rather than an unknown one. */
const fmtTokens = (n?: number | null): string => {
  if (n == null || !Number.isFinite(n)) return '-';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const fmtElapsed = (seconds?: number | null): string => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '-';
  const s = Math.round(seconds);
  const m = Math.floor(s / 60);
  return m ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${s}s`;
};

const Leaderboard: React.FC<LeaderboardProps> = ({
  competitionId,
  publicMode = false,
  leaderboardMode,
  competitionMode,
}) => {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const isRubricLeaderboard = leaderboardData.some((entry) => entry.evaluation_mode === 'rubric');
  const rubricCriteria = leaderboardData[0]?.criterion_scores || [];
  const isFinalMode = (leaderboardMode || '').toLowerCase() === 'final';
  const isTeamMode = (competitionMode || '').toLowerCase() === 'team';

  const getInitials = (nickname?: string, username?: string) => {
    const display = (nickname || username || '').trim();
    const parts = display.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return display.slice(0, 2).toUpperCase() || 'IK';
  };

  const getAvatarColor = (seed: string) => {
    const palette = ['#1f4f82', '#0d5d78', '#7a3e0d', '#365f28', '#6a1f3b', '#4c3b87']; // theme-ok: avatar fills carry white initials in both themes
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  const toKey = (entry: LeaderboardEntry, rank: number) => `${entry.username}-${rank}`;

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      setError('');
      setExpandedKey(null);
      try {
        const response = isFinalMode
          ? (publicMode
              ? await apiService.get(`/evaluations/public/finalists/${competitionId}`)
              : await apiService.get(`/evaluations/finalists/${competitionId}`, {
                  headers: {
                    Authorization: `Bearer ${authUtils.getStoredToken()}`,
                  },
                }))
          : (publicMode
              ? await apiService.get(`/evaluations/public/leaderboard/${competitionId}`)
              : await apiService.post(`/evaluations/leaderboard/${competitionId}`, {}, {
                  headers: {
                    Authorization: `Bearer ${authUtils.getStoredToken()}`,
                  },
                }));
        const dataWithEmojis = response.data.map((entry: any, index: number) => {
          let emoji = '';
          if (index === 0) emoji = '🏆';
          else if (index === 1) emoji = '🥈';
          else if (index === 2) emoji = '🥉';
          else emoji = '🎖️';
          return { ...entry, emoji };
        });
        setLeaderboardData(dataWithEmojis);
      } catch (err) {
        setError('Failed to load leaderboard');
        console.error('Error fetching leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };
    if (competitionId) {
      fetchLeaderboard();
    }
  }, [competitionId, publicMode, isFinalMode]);

  const finalists = useMemo(() => leaderboardData.slice(0, 5), [leaderboardData]);
  const topFinalists = useMemo(() => finalists.slice(0, 3), [finalists]);
  const runnerupFinalists = useMemo(() => finalists.slice(3), [finalists]);

  if (loading) {
    return <div className="leaderboard-container">Loading leaderboard...</div>;
  }

  if (error) {
    return <div className="leaderboard-container">Error: {error}</div>;
  }

  const renderTeamMembers = (entry: LeaderboardEntry, rank: number) => {
    if (!isTeamMode || !Array.isArray(entry.team_members) || entry.team_members.length === 0) return null;
    const key = toKey(entry, rank);
    const isExpanded = expandedKey === key;
    return (
      <div className="leaderboard-final-card__members-wrap">
        <button
          type="button"
          className="leaderboard-final-card__members-toggle"
          onClick={() => setExpandedKey(isExpanded ? null : key)}
        >
          {isExpanded ? 'Hide team members' : `Show team members (${entry.team_members.length})`}
        </button>
        {isExpanded && (
          <div className="leaderboard-final-card__members">
            {entry.team_members.map((member, idx) => (
              <span key={`${member.username}-${idx}`} className="leaderboard-final-card__member-pill">
                {member.nickname || member.username}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (isFinalMode) {
    return (
      <div className="leaderboard-container leaderboard-container--final">
        <section className="leaderboard-final">
          <div className="leaderboard-final__hero">
            <p className="leaderboard-final__kicker">Leaderboard</p>
            <h2 className="leaderboard-final__title">Finalists</h2>
          </div>

          {finalists.length === 0 ? (
            <div className="leaderboard-final__empty">No final candidates available yet.</div>
          ) : (
            <>
              <div className="leaderboard-final__podium">
                {topFinalists.map((entry, index) => {
                  const rank = index + 1;
                  const tone = rank === 1 ? 'gold' : rank === 2 ? 'silver' : 'bronze';
                  return (
                    <article
                      key={toKey(entry, rank)}
                      className={`leaderboard-final-card leaderboard-final-card--${tone} leaderboard-final-card--rank-${rank}`}
                    >
                      <div className="leaderboard-final-card__rank">#{rank}</div>
                      <div className="leaderboard-final-card__emoji">{entry.emoji}</div>
                      <div className="leaderboard-final-card__identity">
                        <span
                          className="leaderboard-final-card__avatar"
                          style={{ backgroundColor: getAvatarColor(entry.username) }}
                          aria-hidden="true"
                        >
                          {getInitials(entry.nickname, entry.username)}
                        </span>
                        <div className="leaderboard-final-card__text">
                          <h3>{entry.nickname || entry.username}</h3>
                          <p>{isTeamMode ? 'Team finalist' : 'Finalist'}</p>
                        </div>
                      </div>
                      <div className="leaderboard-final-card__score">
                        <span>Score</span>
                        <strong>{entry.score.toFixed(2)}{entry.score_max ? ` / ${entry.score_max}` : ''}</strong>
                      </div>
                      {entry.submission_url ? (
                        <a
                          href={entry.submission_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="leaderboard-final-card__submission-link"
                        >
                          View submission
                        </a>
                      ) : null}
                      {renderTeamMembers(entry, rank)}
                    </article>
                  );
                })}
              </div>

              {runnerupFinalists.length > 0 && (
                <div className="leaderboard-final__runnerups">
                  {runnerupFinalists.map((entry, index) => {
                    const rank = index + 4;
                    return (
                      <article
                        key={toKey(entry, rank)}
                        className={`leaderboard-final-card leaderboard-final-card--runnerup leaderboard-final-card--rank-${rank}`}
                      >
                        <div className="leaderboard-final-card__rank">#{rank}</div>
                        <div className="leaderboard-final-card__emoji">{entry.emoji}</div>
                        <div className="leaderboard-final-card__identity">
                          <span
                            className="leaderboard-final-card__avatar"
                            style={{ backgroundColor: getAvatarColor(entry.username) }}
                            aria-hidden="true"
                          >
                            {getInitials(entry.nickname, entry.username)}
                          </span>
                          <div className="leaderboard-final-card__text">
                            <h3>{entry.nickname || entry.username}</h3>
                            <p>{isTeamMode ? 'Team finalist' : 'Finalist'}</p>
                          </div>
                        </div>
                        <div className="leaderboard-final-card__score">
                          <span>Score</span>
                          <strong>{entry.score.toFixed(2)}{entry.score_max ? ` / ${entry.score_max}` : ''}</strong>
                        </div>
                        {entry.submission_url ? (
                          <a
                            href={entry.submission_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="leaderboard-final-card__submission-link"
                          >
                            View submission
                          </a>
                        ) : null}
                        {renderTeamMembers(entry, rank)}
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <h2 className="leaderboard-title">Leaderboard</h2>
      <table className="leaderboard-table">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Participant</th>
            <th>{isRubricLeaderboard ? 'Overall / 5' : 'Score'}</th>
            {isRubricLeaderboard ? <>
              {rubricCriteria.map((item) => <th key={item.criterion_id}>{item.label}</th>)}
              <th>Evaluations</th>
            </> : <><th className="leaderboard__num">Tokens</th><th className="leaderboard__num">Elapsed</th></>}
            <th />
          </tr>
        </thead>
        <tbody>
          {leaderboardData.map(({ username, nickname, score, tokens, duration_seconds, emoji, evaluation_count, criterion_scores }, index) => (
            <tr key={`${username}-${index}`}>
              <td>{index + 1}</td>
              <td>
                <div className="leaderboard-user">
                  <span
                    className="leaderboard-avatar"
                    style={{ backgroundColor: getAvatarColor(username) }}
                    aria-hidden="true"
                  >
                    {getInitials(nickname, username)}
                  </span>
                  <div className="leaderboard-user__text">
                    <span className="leaderboard-user__nickname">{nickname || username}</span>
                  </div>
                </div>
              </td>
              <td>{Number.isFinite(score) ? score.toFixed(2) : '-'}</td>
              {isRubricLeaderboard ? <>
                {rubricCriteria.map((item) => <td key={item.criterion_id}>{Number(criterion_scores?.find((score) => score.criterion_id === item.criterion_id)?.average_score).toFixed(2)}</td>)}
                <td>{evaluation_count}</td>
              </> : <><td className="leaderboard__num">{fmtTokens(tokens)}</td><td className="leaderboard__num">{fmtElapsed(duration_seconds)}</td></>}
              <td className="emoji-cell">{emoji}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {leaderboardData.length === 0 && (
        <p className="leaderboard-empty">
          No scores yet.
        </p>
      )}
    </div>
  );
};

export default Leaderboard;
